import { useCurrentPeriodFilter, usePreviousPeriodFilter } from '@va/dashboard/util-hooks';
import { post } from '@va/http-client';
import { useFetchDataParams, useLazyDataFetch } from '@va/util/hooks';

import { PrivacyLevelsEnum } from '@va/standalone/shared/types';
import { DeviceTypesEnum } from '@va/types/device';
import { Where } from '@va/types/filters';
import { DateRange, PaginatedResponse, Sorting } from '@va/types/requests';
import { Pagination } from '@va/types/table';
import { TimePeriod } from '@va/types/time';
import { useCallback, useMemo } from 'react';

export enum VisitorType {
  NewVisitor = 'new',
  Returning = 'returning',
}

export enum CompanyType {
  HOSTING = 'hosting',
  EDUCATION = 'education',
  GOVERNMENT = 'government',
  BANKING = 'banking',
  BUSINESS = 'business',
  ISP = 'isp',
}

type Session = {
  date: string; // special dimension for timeseries response

  sessionKey: string;
  channel: string;
  category: string;
  adCampaignLabel: string;
  adCampaignSource: string;
  adCampaignMedium: string;
  adCampaignContent: string;
  adCampaignSourcePlatform: string;
  adCampaignCreativeFormat: string;
  adCampaignMarketingTactic: string;
  adCampaignKey: string;
  adCampaignTerm: string;
  referrerUrl: string;
  browserName: string;
  browserVersion: string;
  countryName: string;
  countryCode: string;
  cityName: string;
  cityCode: string;
  deviceType: DeviceTypesEnum;
  display: string;
  platform: string;
  ip: string;
  companyOrgName: string | null;
  companyOrgType: CompanyType | null;
  visitedUrls: string[];
  sessionStartUnix: number;
  landingPageUrl: string;
  latitude: number;
  longitude: number;
  visitorType: VisitorType;
  visitorKey: string;
  hasRecording: boolean;
  isRecordingSeen: boolean;
  isRecordingStarred: boolean;
  hasUTMCampaign: boolean;
  unfilteredVisitCount: number;
  visitCount: number;
  pages: string;
  unfilteredPages: string;
  landingPageTitle: string;
  durationSeconds: string;
  hasThirdPartyReferrer: string;
  conversionEventCount: string;
  isConvertingSession: boolean;
  pageUrl: string;
  pageTitle: string;
  privacyLevel: PrivacyLevelsEnum;
  userAgent: string;
};

export type SessionDimensions = keyof Session | never;
export type SessionMetrics =
  | 'pageCount'
  | 'sessionCount'
  | 'activeSessionCount'
  | 'previousSessionCount'
  | 'sessionCountPercentageChange'
  | 'sessionPercentOfTotal'
  | 'visitCount'
  | 'previousVisitCount'
  | 'visitCountPercentageChange'
  | 'visitorCount'
  | 'previousVisitorCount'
  | 'visitorCountPercentageChange'
  | 'bouncedSessionCount'
  | 'bounceRate'
  | 'advancedBounceRate'
  | 'overallAdvancedBounceRate'
  | 'overallBounceRate'
  | 'averageSessionsPerVisitor'
  | 'overallAverageSessionsPerVisitor'
  | 'averageVisitsPerSession'
  | 'overallAverageVisitsPerSession'
  | 'averageSessionDuration'
  | 'overallAverageSessionDuration'
  | 'competitionAverageSessionCount'
  | 'competitionAverageVisitCount'
  | 'competitionAverageVisitorCount'
  | 'averageSessionActiveViewportDuration'
  | 'overallAverageSessionActiveViewportDuration';

// Define differences between dimension name and returned model property's name
type DimensionToModelMap = {};

type MapDimensionToModel<D extends SessionDimensions> = D extends keyof DimensionToModelMap
  ? DimensionToModelMap[D]
  : D;

export type SessionMetricsResponse<M extends SessionMetrics, D extends SessionDimensions = never> = {
  [Key in D as MapDimensionToModel<Key>]: MapDimensionToModel<Key> extends keyof Session
    ? Session[MapDimensionToModel<Key>]
    : never;
} & {
  [Key in M]: number;
};

export type SessionMetricsPayload<M extends SessionMetrics[], D extends SessionDimensions[] = never> = {
  /**
   * The property to group sessions by
   */
  dimensions?: D;
  /**
   * Metrics to be included in the response
   */
  metrics: M;
  dateRange: DateRange;
  granularity?: TimePeriod | '5min';
  previousDateRange?: DateRange;
  order?: Sorting<M[number]>[];
  pagination?: Pagination;
  where?: Where;
};

type HelperParams<T> = {
  /**
   * A query param that will help identify the request in dev tools
   */
  trace?: string;
  controller?: AbortController;
  config?: useFetchDataParams<T>['config'];
};

export const useSessionMetrics = <
  M extends SessionMetrics[],
  D extends SessionDimensions[] = never,
  T = PaginatedResponse<SessionMetricsResponse<M[number], D[number]>[]>,
>(
  {
    trace,
    controller,
    config,
    ...payloadParam
  }: Omit<SessionMetricsPayload<M, D>, 'dateRange' | 'previousDateRange'> & HelperParams<T>,
  shouldFetch: boolean = true,
  mapper?: (response: PaginatedResponse<SessionMetricsResponse<M[number], D[number]>[]>) => T | Promise<T>,
) => {
  const { from, until, websiteId } = useCurrentPeriodFilter();
  const { from: previousFrom, until: previousUntil } = usePreviousPeriodFilter();

  return useSessionMetricsClean(
    {
      websiteId: websiteId,
      trace: trace,
      controller: controller,
      config: config,
      dateRange: { start: from, end: until },
      previousDateRange: { start: previousFrom, end: previousUntil },
      ...payloadParam,
    },
    shouldFetch,
    mapper,
  );
};

export const useSessionMetricsClean = <
  M extends SessionMetrics[],
  D extends SessionDimensions[] = never,
  T = PaginatedResponse<SessionMetricsResponse<M[number], D[number]>[]>,
>(
  {
    trace,
    controller,
    config,
    websiteId,
    ...payloadParam
  }: { websiteId: string } & SessionMetricsPayload<M, D> & HelperParams<T>,
  shouldFetch: boolean = true,
  mapper?: (response: PaginatedResponse<SessionMetricsResponse<M[number], D[number]>[]>) => T | Promise<T>,
) => {
  const payload = useMemo<SessionMetricsPayload<M, D>>(
    () => ({
      ...payloadParam,
    }),
    [payloadParam],
  );

  const fetchFn = useCallback(
    (_: string, websiteId: string, query: Record<string, string>, payload: SessionMetricsPayload<M, D>) => {
      return fetchSessionMetrics(websiteId, query, payload, controller?.signal);
    },
    [controller],
  );

  return useLazyDataFetch(
    shouldFetch ? [`/v2/websites/${websiteId}/analytics/sessions`, websiteId, { trace }, payload] : null,
    config,
    mapper,
    fetchFn,
  );
};

export const fetchSessionMetrics = <M extends SessionMetrics[], D extends SessionDimensions[] = never>(
  websiteId: string,
  query: Record<string, string>,
  payloadParam: SessionMetricsPayload<M, D>,
  signal?: AbortSignal,
) => {
  return post<PaginatedResponse<SessionMetricsResponse<M[number], D[number]>[]>>(
    `/v2/websites/${websiteId}/analytics/sessions`,
    query,
    payloadParam,
    { signal: signal },
  );
};

type SessionPayload<D extends SessionDimensions[]> = {
  /**
   * The properties to included in the response
   */
  dimensions?: D;
  dateRange: DateRange;
  order?: Sorting<D[number]>[];
  pagination?: Pagination;
  where?: Where;
};

export type SessionsResponse<D extends SessionDimensions> = {
  [Key in D as MapDimensionToModel<Key>]: MapDimensionToModel<Key> extends keyof Session
    ? Session[MapDimensionToModel<Key>]
    : never;
};

export const useSessions = <D extends SessionDimensions[], T = PaginatedResponse<SessionsResponse<D[number]>[]>>(
  { trace, config, ...payloadParam }: Omit<SessionPayload<D>, 'dateRange'> & HelperParams<T>,
  shouldFetch: boolean = true,
  mapper?: (response: PaginatedResponse<SessionsResponse<D[number]>[]>) => T,
) => {
  const { from, until, websiteId } = useCurrentPeriodFilter();

  const payload = useMemo<SessionPayload<D>>(
    () => ({
      ...payloadParam,
      dateRange: { start: from, end: until },
    }),
    [from, payloadParam, until],
  );

  const fetchFn = useCallback((url: string, query: Record<string, string>, payload: SessionPayload<D>) => {
    return post(url, query, payload);
  }, []);

  const key = useMemo(
    () => [`/v2/websites/${websiteId}/raw/sessions`, { trace }, payload],
    [payload, trace, websiteId],
  );

  return useLazyDataFetch(shouldFetch ? key : null, config, mapper, fetchFn);
};
